import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Family: Father, Wife & Marwari Roots";
const DESC =
  "Raj Shamani comes from a Marwari business family in Indore. His father and Shamani Industries, his upbringing, marital status, schooling at Daly College and qualification.";
const URL = "https://rajshamani.life/raj-shamani-family";
const OG = OG_ABOUT;

const faq = [
  {
    q: "Who is Raj Shamani's father?",
    a: "Raj Shamani's father runs Shamani Industries, the family's polymers and additives manufacturing business in Indore, Madhya Pradesh. The family keeps a deliberately low public profile, and Raj rarely discusses personal family details in interviews, so no verified biography of his father exists in the public domain.",
  },
  {
    q: "What is Raj Shamani's family background?",
    a: "Raj Shamani comes from a Marwari business family based in Indore, Madhya Pradesh. The family has run Shamani Industries for decades. Growing up around a factory shaped his early understanding of margins, supply chains and B2B distribution — themes he returns to constantly on the Figuring Out podcast.",
  },
  {
    q: "Is Raj Shamani married?",
    a: "Raj Shamani has not publicly announced a marriage, and there is no verified information about a wife or partner. Reports claiming otherwise are unsourced. He keeps his personal life private and focuses public communication on business.",
  },
  {
    q: "What is Raj Shamani's education and qualification?",
    a: "Raj Shamani completed his schooling at Daly College, Indore — founded in 1870 and one of India's oldest and most prestigious boarding schools. He did not follow a conventional MBA route. He joined Shamani Industries at 16 and learned procurement, manufacturing and B2B sales hands-on, which he describes as his real qualification.",
  },
  {
    q: "Which school did Raj Shamani study at?",
    a: "Raj Shamani studied at Daly College in Indore, Madhya Pradesh, a boarding school established in 1870 that has educated generations of Indian business families.",
  },
  {
    q: "Does Raj Shamani have siblings?",
    a: "Raj Shamani has not publicly discussed siblings, and no verified information is available. Consistent with the rest of his family, those details are kept private.",
  },
  {
    q: "Where is the Shamani family from?",
    a: "The Shamani family is based in Indore, Madhya Pradesh, where Shamani Industries operates. Raj was born and raised there before splitting his time between Indore and Mumbai for the podcast and business work.",
  },
];

export const Route = createFileRoute("/raj-shamani-family")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani family and education", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FamilyPage,
});

function FamilyPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Family" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Users size={14} /> Family &amp; education
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">family</span>, father &amp; education
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani was born into a Marwari business family in Indore, Madhya Pradesh. His
            father runs <strong className="text-foreground">Shamani Industries</strong>, the
            family&apos;s polymers and additives manufacturing company, which Raj joined at 16 and
            where he is now a director. He studied at{" "}
            <strong className="text-foreground">Daly College, Indore</strong>. This page separates
            what is actually verified from the speculation that circulates online.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Family and education at a glance</h2>
        <dl className="rounded-xl border border-border bg-card divide-y divide-border/60">
          {[
            ["Family background", "Marwari business family, Indore, Madhya Pradesh"],
            ["Father", "Runs Shamani Industries (details kept private)"],
            ["Family business", "Shamani Industries — polymers and additives manufacturing"],
            ["School", "Daly College, Indore (founded 1870)"],
            ["Qualification", "Schooling at Daly College; no conventional MBA"],
            ["Marital status", "Not publicly disclosed"],
            ["Based in", "Indore and Mumbai, India"],
          ].map(([k, v]) => (
            <div key={k} className="px-6 py-4 flex flex-wrap gap-2 justify-between">
              <dt className="text-sm uppercase tracking-wide text-gold">{k}</dt>
              <dd className="text-muted-foreground sm:text-right">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          The family business: how Shamani Industries shaped him
        </h2>
        <p className="text-muted-foreground max-w-3xl leading-relaxed mb-4">
          Long before the podcast, Raj Shamani&apos;s education happened on a factory floor. He
          formally joined Shamani Industries at 16, working through procurement, production
          planning and B2B distribution. That grounding is why his content leans towards unit
          economics, cash cycles and distribution rather than the pitch-deck vocabulary of most
          startup commentary.
        </p>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          The Marwari business ethos he grew up with — frugality, long-term relationships,
          reinvestment over display — shows up repeatedly in his interviews and in{" "}
          <Link to="/build-dont-talk" className="text-gold underline underline-offset-4">
            Build, Don&apos;t Talk
          </Link>
          . You can read more about the company itself on the{" "}
          <Link to="/shamani-industries" className="text-gold underline underline-offset-4">
            Shamani Industries page
          </Link>
          .
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">A note on privacy</h2>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          Many pages claiming to list Raj Shamani&apos;s father&apos;s name, wife, siblings or
          family net worth are unsourced. The Shamani family has chosen not to make those details
          public, and this page does not invent them. Where something has not been confirmed by Raj
          himself, it is marked as not disclosed rather than guessed.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-muted-foreground">
          See also{" "}
          <Link to="/raj-shamani-age" className="text-gold underline underline-offset-4">
            Raj Shamani&apos;s age and date of birth
          </Link>{" "}
          and the full{" "}
          <Link to="/about" className="text-gold underline underline-offset-4">
            biography
          </Link>
          .
        </p>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-family" />

      <RelatedPages pages={["aion", "about", "faq", "ventures", "book", "podcast"]} />
    </div>
  );
}
